export const WALKABOUT_JOYSTICK_TUNING = Object.freeze({
  deadzoneCssPx: 10,
  fullSpeedCssPx: 42,
} as const);

export type WalkaboutInputMode = "none" | "keyboard" | "pointer";

export interface WalkaboutIntent {
  readonly x: number;
  readonly z: number;
  readonly magnitude: number;
  readonly inputMode: WalkaboutInputMode;
}

export type WalkaboutIntentListener = (intent: Readonly<WalkaboutIntent>) => void;

export interface WalkaboutPointerSurface extends EventTarget {
  setPointerCapture?(pointerId: number): void;
  releasePointerCapture?(pointerId: number): void;
  hasPointerCapture?(pointerId: number): boolean;
}

export interface WalkaboutVisibilityTarget extends EventTarget {
  readonly visibilityState?: DocumentVisibilityState;
}

export interface CreateWalkaboutInputOptions {
  readonly surface: WalkaboutPointerSurface;
  readonly joystick?: WalkaboutPointerSurface;
  readonly windowTarget?: EventTarget;
  readonly documentTarget?: WalkaboutVisibilityTarget;
}

export interface WalkaboutInputController {
  getIntent(): Readonly<WalkaboutIntent>;
  subscribe(listener: WalkaboutIntentListener): () => void;
  dispose(): void;
}

const EMPTY_INTENT: Readonly<WalkaboutIntent> = Object.freeze({
  x: 0,
  z: 0,
  magnitude: 0,
  inputMode: "none",
});

const MOVEMENT_KEYS = Object.freeze({
  KeyW: "up",
  ArrowUp: "up",
  KeyS: "down",
  ArrowDown: "down",
  KeyA: "left",
  ArrowLeft: "left",
  KeyD: "right",
  ArrowRight: "right",
} as const);

type MovementKey = keyof typeof MOVEMENT_KEYS;

interface KeyboardEventData extends Event {
  readonly code?: string;
  readonly altKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly shiftKey?: boolean;
}

interface PointerEventData extends Event {
  readonly pointerId?: number;
  readonly clientX?: number;
  readonly clientY?: number;
  readonly button?: number;
  readonly isPrimary?: boolean;
}

function isMovementKey(code: string | undefined): code is MovementKey {
  return code !== undefined && Object.hasOwn(MOVEMENT_KEYS, code);
}

function isReservedUiTarget(target: EventTarget | undefined, ownedSurface: EventTarget): boolean {
  if (target === undefined || target === ownedSurface) {
    return false;
  }

  const candidate = target as EventTarget & { closest?: (selectors: string) => unknown };
  return candidate.closest?.(
    "input, textarea, select, button, a[href], [contenteditable]:not([contenteditable='false']), " +
      "[role='button'], [role='checkbox'], [role='menuitem'], [role='option'], [role='radio'], " +
      "[role='slider'], [role='switch'], [role='tab'], [role='textbox']",
  ) != null;
}

function normalizedVector(x: number, z: number): Readonly<{ x: number; z: number; magnitude: number }> {
  const rawMagnitude = Math.hypot(x, z);
  if (rawMagnitude === 0) {
    return Object.freeze({ x: 0, z: 0, magnitude: 0 });
  }

  if (rawMagnitude <= 1) {
    return Object.freeze({ x, z, magnitude: rawMagnitude });
  }

  return Object.freeze({ x: x / rawMagnitude, z: z / rawMagnitude, magnitude: 1 });
}

function pointerVector(deltaX: number, deltaY: number): Readonly<{ x: number; z: number; magnitude: number }> {
  const distance = Math.hypot(deltaX, deltaY);
  if (distance <= WALKABOUT_JOYSTICK_TUNING.deadzoneCssPx) {
    return Object.freeze({ x: 0, z: 0, magnitude: 0 });
  }

  const magnitude = Math.min(
    1,
    (distance - WALKABOUT_JOYSTICK_TUNING.deadzoneCssPx) /
      (WALKABOUT_JOYSTICK_TUNING.fullSpeedCssPx - WALKABOUT_JOYSTICK_TUNING.deadzoneCssPx),
  );
  return Object.freeze({
    x: (deltaX / distance) * magnitude,
    z: (deltaY / distance) * magnitude,
    magnitude,
  });
}

function sameIntent(left: Readonly<WalkaboutIntent>, right: Readonly<WalkaboutIntent>): boolean {
  return left.x === right.x &&
    left.z === right.z &&
    left.magnitude === right.magnitude &&
    left.inputMode === right.inputMode;
}

export function createWalkaboutInput(options: CreateWalkaboutInputOptions): WalkaboutInputController {
  const pointerSurface = options.joystick ?? options.surface;
  const keyboardTarget = options.windowTarget ?? window;
  const lifecycleTarget = options.windowTarget ?? window;
  const visibilityTarget = options.documentTarget ?? document;
  const listeners = new Set<WalkaboutIntentListener>();
  const heldKeys = new Set<MovementKey>();
  const removals: Array<() => void> = [];

  let intent = EMPTY_INTENT;
  let pointerId: number | null = null;
  let pointerOriginX = 0;
  let pointerOriginY = 0;
  let pointerIntent: Readonly<{ x: number; z: number; magnitude: number }> =
    Object.freeze({ x: 0, z: 0, magnitude: 0 });
  let disposed = false;

  const publish = (): void => {
    let next: Readonly<WalkaboutIntent>;
    if (pointerId !== null) {
      next = Object.freeze({ ...pointerIntent, inputMode: "pointer" as const });
    } else {
      const x = Number(heldKeys.has("KeyD") || heldKeys.has("ArrowRight")) -
        Number(heldKeys.has("KeyA") || heldKeys.has("ArrowLeft"));
      const z = Number(heldKeys.has("KeyS") || heldKeys.has("ArrowDown")) -
        Number(heldKeys.has("KeyW") || heldKeys.has("ArrowUp"));
      const keyboardIntent = normalizedVector(x, z);
      next = keyboardIntent.magnitude === 0
        ? EMPTY_INTENT
        : Object.freeze({ ...keyboardIntent, inputMode: "keyboard" as const });
    }

    if (sameIntent(intent, next)) {
      return;
    }
    intent = next;
    for (const listener of [...listeners]) {
      listener(intent);
    }
  };

  const releaseCapture = (ownedPointerId: number): void => {
    if (pointerSurface.hasPointerCapture?.(ownedPointerId) === false) {
      return;
    }
    try {
      pointerSurface.releasePointerCapture?.(ownedPointerId);
    } catch {
      // Capture may already have been released by the user agent.
    }
  };

  const clearPointer = (ownedPointerId: number, shouldReleaseCapture: boolean): void => {
    if (pointerId !== ownedPointerId) {
      return;
    }
    pointerId = null;
    pointerIntent = Object.freeze({ x: 0, z: 0, magnitude: 0 });
    if (shouldReleaseCapture) {
      releaseCapture(ownedPointerId);
    }
    publish();
  };

  const clearAll = (): void => {
    const ownedPointerId = pointerId;
    pointerId = null;
    pointerIntent = Object.freeze({ x: 0, z: 0, magnitude: 0 });
    heldKeys.clear();
    if (ownedPointerId !== null) {
      releaseCapture(ownedPointerId);
    }
    publish();
  };

  const onKeyDown: EventListener = (rawEvent) => {
    const event = rawEvent as KeyboardEventData;
    const eventOrigin = event.composedPath()[0];
    if (
      disposed ||
      !isMovementKey(event.code) ||
      event.altKey === true ||
      event.ctrlKey === true ||
      event.metaKey === true ||
      event.shiftKey === true ||
      isReservedUiTarget(eventOrigin, options.surface)
    ) {
      return;
    }
    event.preventDefault();
    heldKeys.add(event.code);
    publish();
  };

  const onKeyUp: EventListener = (rawEvent) => {
    const event = rawEvent as KeyboardEventData;
    if (disposed || !isMovementKey(event.code) || !heldKeys.has(event.code)) {
      return;
    }
    event.preventDefault();
    heldKeys.delete(event.code);
    publish();
  };

  const onPointerDown: EventListener = (rawEvent) => {
    const event = rawEvent as PointerEventData;
    if (
      disposed ||
      pointerId !== null ||
      typeof event.pointerId !== "number" ||
      typeof event.clientX !== "number" ||
      typeof event.clientY !== "number" ||
      (event.button !== undefined && event.button !== 0) ||
      event.isPrimary === false
    ) {
      return;
    }
    event.preventDefault();
    pointerId = event.pointerId;
    pointerOriginX = event.clientX;
    pointerOriginY = event.clientY;
    pointerIntent = Object.freeze({ x: 0, z: 0, magnitude: 0 });
    pointerSurface.setPointerCapture?.(event.pointerId);
    publish();
  };

  const onPointerMove: EventListener = (rawEvent) => {
    const event = rawEvent as PointerEventData;
    if (
      disposed ||
      pointerId === null ||
      event.pointerId !== pointerId ||
      typeof event.clientX !== "number" ||
      typeof event.clientY !== "number"
    ) {
      return;
    }
    event.preventDefault();
    pointerIntent = pointerVector(event.clientX - pointerOriginX, event.clientY - pointerOriginY);
    publish();
  };

  const onPointerEnd: EventListener = (rawEvent) => {
    const event = rawEvent as PointerEventData;
    if (disposed || typeof event.pointerId !== "number" || event.pointerId !== pointerId) {
      return;
    }
    event.preventDefault();
    clearPointer(event.pointerId, true);
  };

  const onLostPointerCapture: EventListener = (rawEvent) => {
    const event = rawEvent as PointerEventData;
    if (!disposed && typeof event.pointerId === "number") {
      clearPointer(event.pointerId, false);
    }
  };

  const onBlur: EventListener = () => {
    if (!disposed) {
      clearAll();
    }
  };

  const onVisibilityChange: EventListener = () => {
    if (!disposed && visibilityTarget.visibilityState === "hidden") {
      clearAll();
    }
  };

  const listen = (target: EventTarget, type: string, listener: EventListener, options?: AddEventListenerOptions): void => {
    target.addEventListener(type, listener, options);
    removals.push(() => target.removeEventListener(type, listener, options));
  };

  listen(keyboardTarget, "keydown", onKeyDown);
  listen(keyboardTarget, "keyup", onKeyUp);
  listen(pointerSurface, "pointerdown", onPointerDown, { passive: false });
  listen(pointerSurface, "pointermove", onPointerMove, { passive: false });
  listen(pointerSurface, "pointerup", onPointerEnd, { passive: false });
  listen(pointerSurface, "pointercancel", onPointerEnd, { passive: false });
  listen(pointerSurface, "lostpointercapture", onLostPointerCapture);
  listen(lifecycleTarget, "blur", onBlur);
  listen(visibilityTarget, "visibilitychange", onVisibilityChange);

  return Object.freeze({
    getIntent: () => intent,
    subscribe: (listener: WalkaboutIntentListener) => {
      if (disposed) {
        return () => undefined;
      }
      listeners.add(listener);
      let subscribed = true;
      return () => {
        if (subscribed) {
          subscribed = false;
          listeners.delete(listener);
        }
      };
    },
    dispose: () => {
      if (disposed) {
        return;
      }
      for (const remove of removals.splice(0)) {
        remove();
      }
      clearAll();
      disposed = true;
      listeners.clear();
    },
  });
}
